import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { AuthContext } from './AuthContext';

export const BrandContext = createContext({
  profile: null,
  profileExists: false,
  loading: true,
  error: null,
  refresh: async () => {},
  setProfile: () => {},
  clearProfile: () => {},
});

export default function BrandProvider({ children }) {
  const { token, user, loading: authLoading } = useContext(AuthContext);
  const [profile, setProfileState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const refresh = useCallback(async () => {
    if (authLoading) return;
    if (!token && !user) {
      setProfileState(null);
      setError(null);
      setLoading(false);
      setInitialized(true);
      return;
    }
    setLoading(true);
    try {
      const res = await api.brandGetProfile();
      if (res?.notFound || res?.status === 404) {
        setProfileState(null);
        setError(null);
        return;
      }
      if (res?.error) {
        setProfileState(null);
        setError(res.error);
        return;
      }
      const next = res?.item || res?.profile || null;
      setProfileState(next || null);
      setError(null);
    } catch (err) {
      setProfileState(null);
      setError(err?.message || 'brand_profile_unavailable');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [authLoading, token, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const clearProfile = useCallback(() => {
    setProfileState(null);
  }, []);

  const setProfile = useCallback((next) => {
    setProfileState(next || null);
  }, []);

  const value = useMemo(() => ({
    profile,
    profileExists: Boolean(profile && (profile.name || profile.id)),
    loading: !initialized || loading,
    error,
    refresh,
    setProfile,
    clearProfile,
  }), [profile, loading, initialized, error, refresh, setProfile, clearProfile]);

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}
